import { useEffect, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';

import useDebouncedCallback from '../../../../../../common/hooks/useDebouncedCallback';
import useSendDownloadSMS from '../../../../../../common/hooks/useSendDownloadSMS';
import Button from '../../../../../../components/_core/Button';
import locales from './locales';

enum SendSmsButtonState {
  Pristine = 'pristine',
  Sending = 'sending',
  Dirty = 'dirty',
}

const variants = {
  clicked: {
    opacity: 1,
    transition: { duration: 0.3, ease: 'easeOut' },
    y: 0,
  },
  idle: (direction: number) => ({
    opacity: 0,
    transition: { duration: 0.3, ease: 'easeOut' },
    y: direction * 10,
  }),
};

const getSmsButtonContent = (state: SendSmsButtonState) => {
  switch (state) {
    case SendSmsButtonState.Dirty: {
      return locales.dirty;
    }
    case SendSmsButtonState.Sending: {
      return locales.sending;
    }
    case SendSmsButtonState.Pristine:
    default: {
      return locales.pristine;
    }
  }
};

const SendDownloadAppSmsButton = () => {
  const { isSending, sendDownloadSMS } = useSendDownloadSMS(
    'Onboarding_App_Download',
  );
  const [smsButtonState, setSmsButtonState] = useState<SendSmsButtonState>(
    SendSmsButtonState.Pristine,
  );
  const [direction, setDirection] = useState<number>(-1);

  // Prevent multiple SMS actions to be triggered in quick succession
  const setToDirty = useDebouncedCallback(() => {
    setSmsButtonState(SendSmsButtonState.Dirty);
  }, 3000);

  // Link the sms button content changes to the sendDownloadSMS action
  useEffect(() => {
    if (!isSending && smsButtonState === SendSmsButtonState.Pristine) {
      return;
    }

    if (isSending) {
      setSmsButtonState(SendSmsButtonState.Sending);
    } else {
      setToDirty();
    }
  }, [isSending, setToDirty, smsButtonState]);

  // Add transition effect to content changes, changing direction when going from step 3 > 2 and vice versa.
  useEffect(() => {
    if (smsButtonState === SendSmsButtonState.Dirty) {
      setDirection(1);
    } else if (direction === 1) {
      setDirection(-1);
    }
  }, [smsButtonState, direction]);

  return (
    <Button
      isDisabled={smsButtonState === SendSmsButtonState.Sending}
      onClick={() => sendDownloadSMS()}
      variant="inline-secondary"
    >
      <Box height="100%" overflow="hidden" width="100%">
        <AnimatePresence initial={false} mode="wait">
          <motion.div
            animate="clicked"
            custom={direction}
            exit="idle"
            initial="idle"
            key={
              smsButtonState === SendSmsButtonState.Sending ? 'clicked' : 'idle'
            }
            variants={variants}
          >
            {getSmsButtonContent(smsButtonState)}
          </motion.div>
        </AnimatePresence>
      </Box>
    </Button>
  );
};

export default SendDownloadAppSmsButton;
